import type { GalleryImage, PublicProfile, Service } from '@/types'
import { TEMPLATES, getPrimaryPalette, TYPOGRAPHY_OPTIONS } from '@/lib/templates/registry'

/** Shared preview copy so every template compares layout, not sample content. */
export const PREVIEW_BUSINESS_NAME = 'Sample Business'
export const PREVIEW_TAGLINE = 'Your professional tagline here'
export const PREVIEW_BIO =
  'A short description of your business, services, and what makes you unique. This sample text is identical across all template previews so you can compare layouts fairly.'
export const PREVIEW_LOCATION = 'San Francisco, CA'
export const PREVIEW_PHONE = '+1 (555) 123-4567'
export const PREVIEW_EMAIL = 'hello@samplebusiness.com'
export const PREVIEW_ADDRESS = '123 Main Street, San Francisco, CA'
export const PREVIEW_PROFILE_IMAGE =
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face'
export const PREVIEW_COVER_IMAGE =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=600&fit=crop'

const GALLERY_SOURCES = [
  {
    url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=400&fit=crop',
    caption: 'Workspace',
  },
  {
    url: 'https://images.unsplash.com/photo-1521737711862-e3b97375f902?w=400&h=400&fit=crop',
    caption: 'Team collaboration',
  },
  {
    url: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=400&fit=crop',
    caption: 'Client meeting',
  },
] as const

export const PREVIEW_GALLERY: GalleryImage[] = GALLERY_SOURCES.map((item, index) => ({
  id: String(index + 1),
  url: item.url,
  thumbnail_url: item.url.replace('w=400', 'w=200').replace('h=400', 'h=200'),
  caption: item.caption,
  alt_text: item.caption,
  width: 400,
  height: 400,
  sort_order: index,
  is_featured: index === 0,
}))

export const PREVIEW_SERVICES: Service[] = [
  {
    id: '1',
    name: 'Consultation',
    description: 'Initial consultation to discuss your needs',
    price: 75,
    price_display: 'amount',
    duration_minutes: 60,
    is_active: true,
    is_multi_booking: false,
    max_capacity: null,
    category_id: null,
    peak_multiplier: null,
    off_peak_multiplier: null,
  },
  {
    id: '2',
    name: 'Standard Service',
    description: 'Our most popular service package',
    price: 150,
    price_display: 'amount',
    duration_minutes: 90,
    is_active: true,
    is_multi_booking: false,
    max_capacity: null,
    category_id: null,
    peak_multiplier: null,
    off_peak_multiplier: null,
  },
  {
    id: '3',
    name: 'Premium Package',
    description: 'Comprehensive service with premium options',
    price: 299,
    price_display: 'from',
    duration_minutes: 120,
    is_active: true,
    is_multi_booking: false,
    max_capacity: null,
    category_id: null,
    peak_multiplier: null,
    off_peak_multiplier: null,
  },
]

/** Templates available in the appearance settings UI (excludes unreleased social templates). */
export const DASHBOARD_TEMPLATES = TEMPLATES.filter((template) => template.id <= 18)

export function createTemplatePreviewProfile(
  templateId: number,
  options?: { colorScheme?: string; typography?: string },
): PublicProfile {
  const templateMeta =
    DASHBOARD_TEMPLATES.find((template) => template.id === templateId) ??
    DASHBOARD_TEMPLATES[0]
  const primaryPalette = getPrimaryPalette(templateMeta.slug)

  return {
    id: 'preview-profile',
    business_name: PREVIEW_BUSINESS_NAME,
    slug: 'sample-business',
    tagline: PREVIEW_TAGLINE,
    bio: PREVIEW_BIO,
    location: PREVIEW_LOCATION,
    phone: PREVIEW_PHONE,
    email: PREVIEW_EMAIL,
    address: PREVIEW_ADDRESS,
    cover_image: PREVIEW_COVER_IMAGE,
    profile_image: PREVIEW_PROFILE_IMAGE,
    og_image: null,
    meta_title: null,
    meta_description: null,
    categories: [],
    availability: [],
    social_links: [],
    color_scheme: options?.colorScheme ?? primaryPalette?.id ?? 'modern-minimal',
    typography: options?.typography ?? templateMeta.typographyPairs[0] ?? 'playfair-dm-sans',
    gallery: PREVIEW_GALLERY,
    services: PREVIEW_SERVICES,
    template: {
      id: templateMeta.id,
      name: templateMeta.name,
      slug: templateMeta.slug,
      industry:
        templateMeta.industry === 'beauty' ||
        templateMeta.industry === 'creative' ||
        templateMeta.industry === 'trade'
          ? templateMeta.industry
          : 'universal',
      description: templateMeta.description,
      thumbnail_url: null,
      is_premium: false,
      color_palettes: [],
      typography_pairs: templateMeta.typographyPairs.map((id) => ({
        id,
        name: TYPOGRAPHY_OPTIONS[id]?.name ?? id,
      })),
    },
  }
}
